import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Ship, Package, Calculator, Loader2, CheckCircle } from 'lucide-react';
import { formatCurrency } from '@/utils/currency';
import { useToast } from '@/hooks/use-toast';

export default function Costing() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [costingHistory, setCostingHistory] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  // Shipping CH-MV
  const [AB, setAB] = useState(''); // Shipping (Invoice)
  const [AC, setAC] = useState(''); // Total Boxes
  const [CP, setCP] = useState(0); // CP = AB / AC

  // Clearance MV
  const [CL, setCL] = useState(''); // Shipping (Invoice)
  const [TB, setTB] = useState(''); // Total Boxes
  const [TCL, setTCL] = useState(0); // TCL = CL / TB

  // New Item
  const [productName, setProductName] = useState('');
  const [AA, setAA] = useState(''); // Quantity Ordered
  const [A1, setA1] = useState(''); // Price per Unit
  const [A, setA] = useState(0); // A = A1 * 15.42
  const [B, setB] = useState(0); // B = A * 30%
  const [C, setC] = useState(''); // Alibaba Charge
  const [D, setD] = useState(0); // D = C / AA
  const [E, setE] = useState(''); // Delivery Charge
  const [F, setF] = useState(0); // F = E / AA
  const [G, setG] = useState(0); // G = CP
  const [H, setH] = useState(0); // H = G / AA
  const [I, setI] = useState(0); // I = TCL
  const [J, setJ] = useState(0); // J = I / AA
  const [K, setK] = useState(0); // K = A + B + D + F + H + J
  const [L, setL] = useState(30); // Markup %
  const [sellingPrice, setSellingPrice] = useState(0); // = K + (K * L%)

  // Auto-calculations
  useEffect(() => {
    const ab = parseFloat(AB) || 0;
    const ac = parseFloat(AC) || 0;
    setCP(ac > 0 ? ab / ac : 0);
  }, [AB, AC]);

  useEffect(() => {
    const cl = parseFloat(CL) || 0;
    const tb = parseFloat(TB) || 0;
    setTCL(tb > 0 ? cl / tb : 0);
  }, [CL, TB]);

  useEffect(() => {
    const a1 = parseFloat(A1) || 0;
    setA(a1 * 15.42);
  }, [A1]);

  useEffect(() => {
    setB(A * 0.30);
  }, [A]);

  useEffect(() => {
    const c = parseFloat(C) || 0;
    const aa = parseFloat(AA) || 0;
    setD(aa > 0 ? c / aa : 0);
  }, [C, AA]);

  useEffect(() => {
    const e = parseFloat(E) || 0;
    const aa = parseFloat(AA) || 0;
    setF(aa > 0 ? e / aa : 0);
  }, [E, AA]);

  useEffect(() => {
    setG(CP);
  }, [CP]);

  useEffect(() => {
    const aa = parseFloat(AA) || 0;
    setH(aa > 0 ? G / aa : 0);
  }, [G, AA]);

  useEffect(() => {
    setI(TCL);
  }, [TCL]);

  useEffect(() => {
    const aa = parseFloat(AA) || 0;
    setJ(aa > 0 ? I / aa : 0);
  }, [I, AA]);

  useEffect(() => {
    setK(A + B + D + F + H + J);
  }, [A, B, D, F, H, J]);

  useEffect(() => {
    setSellingPrice(K + (K * L / 100));
  }, [K, L]);

  // Fetch products on mount
  useEffect(() => {
    fetchProducts();
    fetchCostingHistory();
  }, []);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/products`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCostingHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/costing`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCostingHistory(data);
      }
    } catch (error) {
      console.error('Error fetching costing history:', error);
    }
  };

  const handleSaveCosting = async () => {
    if (!productName || !AA || !A1) {
      toast({
        title: "Missing Information",
        description: "Please fill in Product Name, Quantity, and Price per Unit",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const costingData = {
        product_name: productName,
        quantity_ordered: parseInt(AA),
        shipping_invoice_chmv: parseFloat(AB) || 0,
        total_boxes_chmv: parseFloat(AC) || 0,
        cost_per_box_chmv: CP,
        shipping_invoice_mv: parseFloat(CL) || 0,
        total_boxes_mv: parseFloat(TB) || 0,
        cost_per_box_mv: TCL,
        price_per_unit_usd: parseFloat(A1),
        price_per_unit_mvr: A,
        bml_30_percent: B,
        alibaba_charge: parseFloat(C) || 0,
        alibaba_charge_per_unit: D,
        delivery_charge: parseFloat(E) || 0,
        delivery_charge_per_unit: F,
        shipping_charge: G,
        shipping_charge_per_unit: H,
        clearance_charge: I,
        clearance_charge_per_unit: J,
        total_cost_per_unit: K,
        markup_percentage: L,
        selling_price_per_unit: sellingPrice
      };

      const response = await fetch(`${BACKEND_URL}/api/costing`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(costingData)
      });

      if (response.ok) {
        const savedEntry = await response.json();
        toast({
          title: "Success",
          description: "Costing entry saved successfully"
        });
        fetchCostingHistory();
        return savedEntry.id;
      } else {
        throw new Error('Failed to save costing entry');
      }
    } catch (error) {
      console.error('Error saving costing:', error);
      toast({
        title: "Error",
        description: "Failed to save costing entry",
        variant: "destructive"
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handlePushToInventory = async () => {
    // First save the costing entry
    const costingId = await handleSaveCosting();
    if (!costingId) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Determine if creating new or updating existing
      const pushData = {
        create_new_product: !selectedProductId,
        product_id: selectedProductId || null
      };

      if (!selectedProductId) {
        // Creating new product - need to provide SKU
        pushData.sku = `SKU-${Date.now()}`;
        pushData.description = `${productName} - Imported from costing`;
        pushData.show_on_website = false;
      }

      const response = await fetch(`${BACKEND_URL}/api/costing/${costingId}/push-to-inventory`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pushData)
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Success",
          description: result.message,
          variant: "default"
        });
        handleReset();
        fetchProducts();
        fetchCostingHistory();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to push to inventory');
      }
    } catch (error) {
      console.error('Error pushing to inventory:', error);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setProductName('');
    setAA('');
    setA1('');
    setC('');
    setE('');
    setL(30);
    setAB('');
    setAC('');
    setCL('');
    setTB('');
    setSelectedProductId('');
  };

  return (
    <Layout>
      <div className="space-y-6 sm:space-y-8 p-4 sm:p-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-emerald-600 to-blue-600 bg-clip-text text-transparent">
            Costing Calculator
          </h1>
        </div>

        {/* Shipment Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Shipping CH-MV */}
          <Card className="p-4 sm:p-6 bg-gradient-to-br from-cyan-50 via-blue-50 to-cyan-100 border-2 border-cyan-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4 sm:mb-6 pb-3 border-b-2 border-cyan-300">
              <Ship className="w-5 h-5 sm:w-6 sm:h-6 text-cyan-700 flex-shrink-0" />
              <h3 className="text-lg sm:text-xl font-bold text-cyan-800">Shipping CH-MV</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs sm:text-sm font-semibold text-cyan-900">Shipping Invoice (USD)</Label>
                <Input
                  type="number"
                  value={AB}
                  onChange={(e) => setAB(e.target.value)}
                  placeholder="Enter USD amount"
                  className="mt-1 border-cyan-300 focus:border-cyan-500 text-sm sm:text-base"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm font-semibold text-cyan-900">Total Boxes (Cartons)</Label>
                <Input
                  type="number"
                  value={AC}
                  onChange={(e) => setAC(e.target.value)}
                  placeholder="Number of cartons"
                  className="mt-1 border-cyan-300 focus:border-cyan-500 text-sm sm:text-base"
                />
              </div>
              <div className="pt-4 border-t-2 border-cyan-200 bg-white/60 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-cyan-800 font-medium mb-2">Cost Per Box (Carton)</p>
                <p className="text-2xl sm:text-3xl font-bold text-cyan-700">{formatCurrency(CP)}</p>
              </div>
            </div>
          </Card>

          {/* Clearance MV */}
          <Card className="p-4 sm:p-6 bg-gradient-to-br from-emerald-50 via-green-50 to-emerald-100 border-2 border-emerald-200 shadow-lg">
            <div className="flex items-center gap-3 mb-4 sm:mb-6 pb-3 border-b-2 border-emerald-300">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-700 flex-shrink-0" />
              <h3 className="text-lg sm:text-xl font-bold text-emerald-800">Clearance MV</h3>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-xs sm:text-sm font-semibold text-emerald-900">Shipping Invoice (USD)</Label>
                <Input
                  type="number"
                  value={CL}
                  onChange={(e) => setCL(e.target.value)}
                  placeholder="Enter USD amount"
                  className="mt-1 border-emerald-300 focus:border-emerald-500 text-sm sm:text-base"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm font-semibold text-emerald-900">Total Boxes (Cartons)</Label>
                <Input
                  type="number"
                  value={TB}
                  onChange={(e) => setTB(e.target.value)}
                  placeholder="Number of cartons"
                  className="mt-1 border-emerald-300 focus:border-emerald-500 text-sm sm:text-base"
                />
              </div>
              <div className="pt-4 border-t-2 border-emerald-200 bg-white/60 rounded-lg p-3 sm:p-4">
                <p className="text-xs sm:text-sm text-emerald-800 font-medium mb-2">Cost Per Box (Carton)</p>
                <p className="text-2xl sm:text-3xl font-bold text-emerald-700">{formatCurrency(TCL)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* New Item Section */}
        <Card className="p-4 sm:p-6 lg:p-8 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-orange-300 shadow-xl">
          <div className="flex items-center gap-3 mb-4 sm:mb-6 pb-3 sm:pb-4 border-b-2 border-orange-300">
            <Calculator className="w-6 h-6 sm:w-7 sm:h-7 text-orange-700 flex-shrink-0" />
            <h3 className="text-xl sm:text-2xl font-bold text-orange-900">New Item</h3>
          </div>

          <div className="space-y-4 sm:space-y-6">
            {/* Product Details */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="text-xs sm:text-sm font-semibold">Product Name</Label>
                <Input
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  placeholder="Must integrate with inventory"
                  className="mt-1 text-sm sm:text-base"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm font-semibold">Quantity Ordered</Label>
                <Input
                  type="number"
                  value={AA}
                  onChange={(e) => setAA(e.target.value)}
                  placeholder="Enter quantity"
                  className="mt-1 text-sm sm:text-base"
                />
              </div>
              <div>
                <Label className="text-xs sm:text-sm font-semibold">Price per Unit (USD)</Label>
                <Input
                  type="number"
                  value={A1}
                  onChange={(e) => setA1(e.target.value)}
                  placeholder="0.00"
                  className="mt-1 text-sm sm:text-base"
                />
              </div>
            </div>

            {/* Calculated Values */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white/70 p-3 sm:p-4 rounded-lg">
              <div>
                <Label className="text-xs sm:text-sm font-semibold text-blue-900">Price in MVR</Label>
                <div className="mt-1 p-2 sm:p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-base sm:text-lg font-bold text-blue-700">{formatCurrency(A)}</p>
                </div>
              </div>
              <div>
                <Label className="text-xs sm:text-sm font-semibold text-purple-900">BML 30% per Unit</Label>
                <div className="mt-1 p-2 sm:p-3 bg-purple-50 border border-purple-200 rounded-md">
                  <p className="text-base sm:text-lg font-bold text-purple-700">{formatCurrency(B)}</p>
                </div>
              </div>
            </div>

            {/* Charges */}
            <div className="space-y-4">
              {/* Alibaba Charge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 sm:p-4 bg-white rounded-lg border border-slate-200">
                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Alibaba Charge (USD)</Label>
                  <Input
                    type="number"
                    value={C}
                    onChange={(e) => setC(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 text-sm sm:text-base"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Per Unit</Label>
                  <div className="mt-1 p-2 sm:p-3 bg-slate-100 rounded-md">
                    <p className="text-base sm:text-lg font-bold">{formatCurrency(D)}</p>
                  </div>
                </div>
              </div>

              {/* Delivery Charge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 sm:p-4 bg-white rounded-lg border border-slate-200">
                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Delivery Charge (USD)</Label>
                  <Input
                    type="number"
                    value={E}
                    onChange={(e) => setE(e.target.value)}
                    placeholder="0.00"
                    className="mt-1 text-sm sm:text-base"
                  />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-semibold">Per Unit</Label>
                  <div className="mt-1 p-2 sm:p-3 bg-slate-100 rounded-md">
                    <p className="text-base sm:text-lg font-bold">{formatCurrency(F)}</p>
                  </div>
                </div>
              </div>

              {/* Shipping Charge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 sm:p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <div>
                  <Label className="text-xs sm:text-sm font-semibold text-cyan-900">Shipping Charge</Label>
                  <div className="mt-1 p-2 sm:p-3 bg-white rounded-md">
                    <p className="text-base sm:text-lg font-bold text-cyan-700">{formatCurrency(G)}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-semibold text-cyan-900">Per Unit</Label>
                  <div className="mt-1 p-2 sm:p-3 bg-white rounded-md">
                    <p className="text-base sm:text-lg font-bold text-cyan-700">{formatCurrency(H)}</p>
                  </div>
                </div>
              </div>

              {/* Clearance Charge */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-3 sm:p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div>
                  <Label className="text-xs sm:text-sm font-semibold text-emerald-900">Clearance Charge</Label>
                  <div className="mt-1 p-2 sm:p-3 bg-white rounded-md">
                    <p className="text-base sm:text-lg font-bold text-emerald-700">{formatCurrency(I)}</p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm font-semibold text-emerald-900">Per Unit</Label>
                  <div className="mt-1 p-2 sm:p-3 bg-white rounded-md">
                    <p className="text-base sm:text-lg font-bold text-emerald-700">{formatCurrency(J)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Markup Percentage */}
            <div className="p-3 sm:p-4 bg-white rounded-lg border border-slate-200">
              <Label className="text-xs sm:text-sm font-semibold">Markup Percentage (%)</Label>
              <Input
                type="number"
                value={L}
                onChange={(e) => setL(parseFloat(e.target.value) || 0)}
                placeholder="30"
                className="mt-1 max-w-xs text-sm sm:text-base"
              />
            </div>

            {/* Final Results */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 pt-4 sm:pt-6 border-t-2 border-orange-300">
              <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl p-4 sm:p-6 shadow-lg">
                <Label className="text-base sm:text-lg font-bold text-blue-900 mb-2 sm:mb-3 block">Total Cost per Unit</Label>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-blue-800">{formatCurrency(K)}</p>
              </div>
              <div className="bg-gradient-to-br from-green-100 to-emerald-200 rounded-xl p-4 sm:p-6 shadow-lg">
                <Label className="text-base sm:text-lg font-bold text-emerald-900 mb-2 sm:mb-3 block">Selling Price per Unit</Label>
                <p className="text-2xl sm:text-3xl lg:text-4xl font-bold text-emerald-800">{formatCurrency(sellingPrice)}</p>
              </div>
            </div>

            {/* Product Selection for Inventory */}
            <div className="p-3 sm:p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
              <Label className="text-xs sm:text-sm font-semibold text-blue-900 mb-2 block">
                Link to Existing Product (Optional)
              </Label>
              <select
                value={selectedProductId}
                onChange={(e) => setSelectedProductId(e.target.value)}
                className="w-full p-2 sm:p-3 border-2 border-blue-300 rounded-md focus:border-blue-500 bg-white text-sm sm:text-base"
              >
                <option value="">Create New Product</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {product.name} (SKU: {product.sku})
                  </option>
                ))}
              </select>
              <p className="text-xs text-blue-700 mt-2">
                Select an existing product to update, or leave empty to create a new product
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Button 
                onClick={handlePushToInventory}
                disabled={loading}
                className="bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-700 hover:to-green-700 text-white font-semibold px-6 sm:px-8 py-2 sm:py-3 text-base sm:text-lg shadow-lg disabled:opacity-50 w-full sm:w-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
                    Push to Inventory
                  </>
                )}
              </Button>
              <Button 
                variant="outline" 
                onClick={handleReset}
                disabled={loading}
                className="border-2 border-slate-300 hover:bg-slate-100 px-6 sm:px-8 py-2 sm:py-3 text-base sm:text-lg font-semibold disabled:opacity-50 w-full sm:w-auto"
              >
                Reset
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Layout>
  );
}
